import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

import i18n from 'i18next'
import { useMemo, useState } from 'react'
import useFetchDocs from '../hooks/useFetchDocs'
import DocumentSkeleton from './DocumentListSkeleton'

import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import GridView from '@mui/icons-material/GridView'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import TableRows from '@mui/icons-material/TableRows'
import TaskIcon from '@mui/icons-material/Task'

import SearchIcon from '@mui/icons-material/Search'
import {
  Alert,
  Autocomplete,
  Badge,
  Button,
  ButtonGroup,
  Chip,
  Divider,
  Snackbar,
  SpeedDial,
  SpeedDialAction,
  TextField,
} from '@mui/material'
import { DocIcon } from 'icons/DocIcon'
import * as Constants from './Constants'
import './css/ListadoDocumentos.css'

import FirmaDocumentosGridView from './FirmaDocumentosGridView'
import FirmaDocumentosTableView from './FirmaDocumentosTableView'

const ListadoDocumentos = () => {
  const defaultErrorState = {
    error: false,
    code: 0,
    msg: '',
  }

  const [trigger, setTrigger] = useState(null)
  const { response, loading } = useFetchDocs(trigger, setTrigger)
  const [selectedDocs, setSelectedDocs] = useState(new Set())
  const [lastResponse, setLastResponse] = useState(response)
  const [filtro, setFiltro] = useState('sinFirmar')
  const [action, setAction] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [openFilter, setOpenFilter] = useState(false)
  const handleOpenFilter = () => setOpenFilter(true)
  const handleCloseFilter = () => setOpenFilter(false)
  const [errorStatus, setErrorStatus] = useState(defaultErrorState)
  const [viewType, setViewType] = useState('TABLE')
  const [filtroCodigoDoc, setFiltroCodigoDoc] = useState(null)

  const param = useMemo(() => response?.param ?? {}, [response])
  const codigosDocumentos = useMemo(() => response?.codigos_documentos ?? [], [response])

  const documentos = useMemo(
    () =>
      (response?.documentos ?? []).map((doc, index) => ({
        ...doc,
        docId: `${doc.empresa}-${doc.codigo_tipo_doc}-${index}`,
        tipos_2fa: param.tipos_2fa ?? [],
        firma_2fa: param.firma_2fa ?? 'N',
        firma_manual: param.firma_manual ?? 'N',
      })),
    [response, param],
  )

  const statusColorMap = useMemo(() => {
    const map = {}
    codigosDocumentos.forEach((cod) => {
      ;(cod.status_documento ?? []).forEach((s) => {
        map[`${cod.codigo}-${s.codigo_status}`] = {
          bgcolor: `#${s.color_fondo_status}`,
          color: `#${s.color_texto_status}`,
          label: s.descripcion_status,
        }
      })
    })
    return map
  }, [codigosDocumentos])

  const tipoDocColorMap = useMemo(() => {
    const map = {}
    codigosDocumentos.forEach((cod) => {
      const color = cod.color_tipo_doc ? `#${cod.color_tipo_doc}` : null
      if (cod.icono_tipo_doc) map[cod.icono_tipo_doc] = color
      map[cod.codigo] = color
    })
    return map
  }, [codigosDocumentos])

  const conteos = useMemo(
    () => ({
      sinFirmar: documentos.filter((d) => d.codigo_status !== Constants.STATUS_CERRADO).length,
      firmados: documentos.filter((d) => d.codigo_status === Constants.STATUS_CERRADO).length,
      todos: documentos.length,
    }),
    [documentos],
  )

  const conteosPorTipo = useMemo(() => {
    const map = {}
    const base =
      filtro === 'firmados'
        ? documentos.filter((d) => d.codigo_status === Constants.STATUS_CERRADO)
        : filtro === 'sinFirmar'
          ? documentos.filter((d) => d.codigo_status !== Constants.STATUS_CERRADO)
          : documentos
    base.forEach((d) => {
      map[d.codigo_tipo_doc] = (map[d.codigo_tipo_doc] || 0) + 1
    })
    return map
  }, [documentos, filtro])

  const textoFiltroMap = {
    sinFirmar: i18n.t('firmaDocumentos.filtroSinFirmar', { defaultValue: 'Sin firmar' }),
    firmados: i18n.t('firmaDocumentos.filtroFirmados', { defaultValue: 'Firmados' }),
    todos: i18n.t('firmaDocumentos.filtroTodos', { defaultValue: 'Todos' }),
  }

  const hayDocumentosParaFirmar = useMemo(
    () =>
      documentos.some((doc) => (doc.ficheros_a_firmar ?? []).some((f) => f.puede_firmar === 'S')),
    [documentos],
  )

  const textoTipoFirma = useMemo(() => {
    if (documentos.length === 0) return ''
    if (param.firma_manual === 'S') {
      return 'Debe descargar el documento, firmarlo por sus medios y aceptar el expediente adjuntando el fichero firmado.'
    }
    if (param.firma_2fa === 'S') {
      return 'Debe pulsar el bot\u00f3n de firmar y validar su firma con un doble factor.'
    }
    return ''
  }, [documentos, param])

  const documentosBuscador = useMemo(
    () =>
      documentos.map((doc, index) => ({
        label: doc.descripcion,
        id: index,
      })),
    [documentos],
  )

  if (response !== lastResponse) {
    setLastResponse(response)
    setSelectedDocs(new Set())
  }

  const handleClose = (reason) => {
    if (reason === 'clickaway') {
      return
    }
    setErrorStatus(defaultErrorState)
  }

  const documentosFiltrados = useMemo(() => {
    if (!documentos || documentos.length === 0) return []

    let resultado = documentos

    if (busqueda && busqueda.trim() !== '') {
      resultado = resultado.filter((doc) =>
        doc.descripcion.toLocaleLowerCase().includes(busqueda.toLocaleLowerCase()),
      )
    }

    if (filtro === 'firmados') {
      resultado = resultado.filter((doc) => doc.codigo_status === Constants.STATUS_CERRADO)
    } else if (filtro === 'sinFirmar') {
      resultado = resultado.filter((doc) => doc.codigo_status !== Constants.STATUS_CERRADO)
    }

    if (filtroCodigoDoc) {
      resultado = resultado.filter((doc) => doc.codigo_tipo_doc === filtroCodigoDoc)
    }

    return resultado
  }, [documentos, busqueda, filtro, filtroCodigoDoc])

  const hasDocumentos = documentos && documentos.length > 0

  const alertSinFicheros = (
    <Box sx={{ flexDirection: 'column' }}>
      <Alert variant="outlined" severity="info" sx={{ mb: 2 }}>
        Sin documentos para firmar.
      </Alert>
    </Box>
  )

  const viewProps = {
    documentos: documentosFiltrados,
    loading,
    selectedDocs,
    setSelectedDocs,
    setAction,
    action,
    setTrigger,
    setErrorStatus,
    statusColorMap,
    tipoDocColorMap,
  }

  return (
    <>
      {!loading && hasDocumentos ? (
        <>
          <div className="gestion-documentos-buscador">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <Autocomplete
                freeSolo
                onInputChange={(_event, newInputValue) => {
                  setBusqueda(newInputValue)
                }}
                id="firma-documentos-busqueda"
                options={documentosBuscador}
                sx={{
                  flex: 1,
                  maxWidth: {
                    xs: '100%',
                    md: '40%',
                    lg: '35%',
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={
                      <p className="gestion-documentos-buscador-label">
                        <SearchIcon /> Buscar documento
                      </p>
                    }
                  />
                )}
              />
              {textoTipoFirma && (filtro === 'sinFirmar' || filtro === 'todos') && (
                <Alert
                  variant="outlined"
                  severity="info"
                  sx={{ flex: 1, display: { xs: 'none', md: 'flex' } }}
                >
                  {textoTipoFirma}
                </Alert>
              )}
              <ButtonGroup sx={{ display: { xs: 'none', md: 'flex' }, ml: 'auto' }}>
                <Button
                  variant={viewType === 'TABLE' ? 'contained' : 'text'}
                  onClick={() => setViewType('TABLE')}
                >
                  <TableRows />
                </Button>
                <Button
                  variant={viewType === 'TABLE' ? 'text' : 'contained'}
                  onClick={() => setViewType('GRID')}
                >
                  <GridView />
                </Button>
              </ButtonGroup>
            </Box>
            {/* Mobile: SpeedDial */}
            <Box
              sx={{
                display: { xs: 'block', md: 'none' },
                position: 'fixed',
                bottom: 30,
                right: 30,
                width: 70,
                textAlign: 'center',
                zIndex: openFilter ? 999 : 0,
              }}
            >
              <SpeedDial
                ariaLabel={i18n.t('firmaDocumentos.ariaFiltros', {
                  defaultValue: 'Filtros de documentos',
                })}
                icon={<FilterAltOutlinedIcon />}
                className="boton-filtros"
                onClose={handleCloseFilter}
                onOpen={handleOpenFilter}
              >
                <SpeedDialAction
                  key="firmados"
                  icon={<TaskIcon />}
                  tooltipTitle={
                    <p className={filtro === 'firmados' ? 'filtro-activo' : ''}>
                      {i18n.t('firmaDocumentos.filtroFirmados', { defaultValue: 'Firmados' })}
                    </p>
                  }
                  tooltipOpen
                  onClick={() => setFiltro('firmados')}
                  className={filtro === 'firmados' ? 'filtro-activo' : ''}
                />
                <SpeedDialAction
                  key="sinFirmar"
                  icon={<InsertDriveFileIcon />}
                  tooltipTitle={
                    <p className={filtro === 'sinFirmar' ? 'filtro-activo' : ''}>
                      {i18n.t('firmaDocumentos.filtroSinFirmar', {
                        defaultValue: 'Sin firmar',
                      })}
                    </p>
                  }
                  tooltipOpen
                  onClick={() => setFiltro('sinFirmar')}
                  className={filtro === 'sinFirmar' ? 'filtro-activo' : ''}
                />
                <SpeedDialAction
                  key="todos"
                  icon={<ContentCopyOutlinedIcon />}
                  tooltipTitle={
                    <p className={filtro === 'todos' ? 'filtro-activo' : ''}>
                      {i18n.t('firmaDocumentos.filtroTodos', { defaultValue: 'Todos' })}
                    </p>
                  }
                  tooltipOpen
                  onClick={() => setFiltro('todos')}
                  className={filtro === 'todos' ? 'filtro-activo' : ''}
                />
                {codigosDocumentos.length >= 1 && [
                  <SpeedDialAction
                    key="todos-tipos"
                    icon={<DescriptionOutlinedIcon />}
                    tooltipTitle={
                      <p className={filtroCodigoDoc === null ? 'filtro-activo' : ''}>
                        {i18n.t('firmaDocumentos.filtroTodosTipos', {
                          defaultValue: 'Todos tipos',
                        })}
                      </p>
                    }
                    tooltipOpen
                    onClick={() => setFiltroCodigoDoc(null)}
                    className={filtroCodigoDoc === null ? 'filtro-activo' : ''}
                  />,
                  ...codigosDocumentos.map((cod) => (
                    <SpeedDialAction
                      key={`tipo-${cod.codigo}`}
                      icon={
                        <DocIcon
                          kind={cod.codigo.toLowerCase()}
                          color={tipoDocColorMap[cod.icono_tipo_doc]}
                          size={20}
                          decorative
                          style={{ transform: 'translateX(3px)' }}
                        />
                      }
                      tooltipTitle={
                        <p className={filtroCodigoDoc === cod.codigo ? 'filtro-activo' : ''}>
                          {cod.descripcion || cod.codigo}
                        </p>
                      }
                      tooltipOpen
                      onClick={() => setFiltroCodigoDoc(cod.codigo)}
                      className={filtroCodigoDoc === cod.codigo ? 'filtro-activo' : ''}
                    />
                  )),
                ]}
              </SpeedDial>
              <Typography
                variant="body2"
                sx={{ position: 'relative', textAlign: 'center', top: 10 }}
              >
                {textoFiltroMap[filtro]}
              </Typography>
            </Box>
          </div>

          {/* Mobile: alert + sin ficheros + type filter */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {textoTipoFirma && (filtro === 'sinFirmar' || filtro === 'todos') ? (
              <Alert sx={{ my: 1 }} variant="outlined" severity="info">
                {textoTipoFirma}
              </Alert>
            ) : !hayDocumentosParaFirmar ? (
              alertSinFicheros
            ) : null}
            {codigosDocumentos.length >= 1 && (
              <Box sx={{ display: 'flex', gap: 1.5, my: 2, flexWrap: 'wrap' }}>
                <Badge badgeContent={conteos[filtro]} color="secondary">
                  <Chip
                    icon={<DescriptionOutlinedIcon />}
                    label={i18n.t('firmaDocumentos.filtroTodosTipos', {
                      defaultValue: 'Todos tipos',
                    })}
                    variant={filtroCodigoDoc === null ? 'filled' : 'outlined'}
                    color={filtroCodigoDoc === null ? 'secondary' : 'default'}
                    size="small"
                    onClick={() => setFiltroCodigoDoc(null)}
                    sx={{ px: 1, height: 40 }}
                  />
                </Badge>
                {codigosDocumentos.map((cod) => (
                  <Badge
                    key={cod.codigo}
                    badgeContent={conteosPorTipo[cod.codigo] || 0}
                    color="secondary"
                  >
                    <Chip
                      icon={
                        <DocIcon
                          kind={cod.codigo.toLowerCase()}
                          color={tipoDocColorMap[cod.icono_tipo_doc]}
                          size={20}
                          decorative
                          style={{ transform: 'translateX(3px)' }}
                        />
                      }
                      label={cod.descripcion || cod.codigo}
                      variant={filtroCodigoDoc === cod.codigo ? 'filled' : 'outlined'}
                      color={filtroCodigoDoc === cod.codigo ? 'secondary' : 'default'}
                      size="small"
                      onClick={() => setFiltroCodigoDoc(cod.codigo)}
                      sx={{ px: 1, height: 40 }}
                    />
                  </Badge>
                ))}
              </Box>
            )}
          </Box>

          {/* Mobile: always cards */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', mb: '120px' }}>
            <FirmaDocumentosGridView {...viewProps} />
          </Box>

          {/* Desktop: sidebar filters + content */}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, mt: 1, mb: '120px' }}>
            {/* Sidebar */}
            <Box sx={{ width: 200, flexShrink: 0 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Estado
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Badge badgeContent={conteos.sinFirmar} color="secondary">
                  <Chip
                    icon={<InsertDriveFileIcon />}
                    label={i18n.t('firmaDocumentos.filtroSinFirmar', {
                      defaultValue: 'Sin firmar',
                    })}
                    variant={filtro === 'sinFirmar' ? 'filled' : 'outlined'}
                    color={filtro === 'sinFirmar' ? 'secondary' : 'default'}
                    onClick={() => setFiltro('sinFirmar')}
                    sx={{ width: '100%', justifyContent: 'flex-start', px: 1 }}
                  />
                </Badge>
                <Badge badgeContent={conteos.firmados} color="secondary">
                  <Chip
                    icon={<TaskIcon />}
                    label={i18n.t('firmaDocumentos.filtroFirmados', {
                      defaultValue: 'Firmados',
                    })}
                    variant={filtro === 'firmados' ? 'filled' : 'outlined'}
                    color={filtro === 'firmados' ? 'secondary' : 'default'}
                    onClick={() => setFiltro('firmados')}
                    sx={{ width: '100%', justifyContent: 'flex-start', px: 1 }}
                  />
                </Badge>
                <Badge badgeContent={conteos.todos} color="secondary">
                  <Chip
                    icon={<ContentCopyOutlinedIcon />}
                    label={i18n.t('firmaDocumentos.filtroTodos', { defaultValue: 'Todos' })}
                    variant={filtro === 'todos' ? 'filled' : 'outlined'}
                    color={filtro === 'todos' ? 'secondary' : 'default'}
                    onClick={() => setFiltro('todos')}
                    sx={{ width: '100%', justifyContent: 'flex-start', px: 1 }}
                  />
                </Badge>
              </Box>

              {codigosDocumentos.length >= 1 && (
                <>
                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    Tipo de documento
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Badge badgeContent={conteos[filtro]} color="secondary">
                      <Chip
                        icon={<DescriptionOutlinedIcon />}
                        label={i18n.t('firmaDocumentos.filtroTodosTipos', {
                          defaultValue: 'Todos tipos',
                        })}
                        variant={filtroCodigoDoc === null ? 'filled' : 'outlined'}
                        color={filtroCodigoDoc === null ? 'secondary' : 'default'}
                        onClick={() => setFiltroCodigoDoc(null)}
                        sx={{ width: '100%', justifyContent: 'flex-start', px: 1 }}
                      />
                    </Badge>
                    {codigosDocumentos.map((cod) => (
                      <Badge
                        key={cod.codigo}
                        badgeContent={conteosPorTipo[cod.codigo] || 0}
                        color="secondary"
                      >
                        <Chip
                          icon={
                            <DocIcon
                              kind={cod.codigo.toLowerCase()}
                              color={tipoDocColorMap[cod.icono_tipo_doc]}
                              size={20}
                              decorative
                              style={{ transform: 'translateX(3px)' }}
                            />
                          }
                          label={cod.descripcion || cod.codigo}
                          variant={filtroCodigoDoc === cod.codigo ? 'filled' : 'outlined'}
                          color={filtroCodigoDoc === cod.codigo ? 'secondary' : 'default'}
                          onClick={() => setFiltroCodigoDoc(cod.codigo)}
                          sx={{ width: '100%', justifyContent: 'flex-start', px: 1 }}
                        />
                      </Badge>
                    ))}
                  </Box>
                </>
              )}
            </Box>

            <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />

            {/* Content */}
            <Box sx={{ flex: 1, flexDirection: 'column' }}>
              {!hayDocumentosParaFirmar && alertSinFicheros}
              {viewType === 'TABLE' ? (
                <FirmaDocumentosTableView {...viewProps} />
              ) : (
                <FirmaDocumentosGridView {...viewProps} />
              )}
            </Box>
          </Box>
        </>
      ) : !hayDocumentosParaFirmar && !loading ? (
        alertSinFicheros
      ) : (
        <DocumentSkeleton spacing={2} />
      )}

      {errorStatus.error && (
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={errorStatus.error}
          autoHideDuration={3000}
          onClose={handleClose}
        >
          <Alert
            variant="filled"
            onClose={handleClose}
            severity={errorStatus.code > 300 ? 'error' : 'success'}
            sx={{ width: '100%' }}
          >
            {errorStatus.msg}
          </Alert>
        </Snackbar>
      )}
    </>
  )
}

export default ListadoDocumentos
